import React, { useState, useEffect } from "react";
import UserPageLayout from "../../../../components/UserPageLayout";
import { useAppContext } from "../../../../../../context/AppContext";
import Pagination from "../../../../../../components/Pagination";
import { UsersData } from "../../../../network/api/UsersData";
import { UserAccess } from "../../../../network/api/UserAccess";
import Alerts from "../../../../../../components/Alerts";
import { Link } from "react-router-dom";
import Toast from "../../../../../../components/Toast";
import { encodeId } from "../../../../../../utils/hash";
import { highlightText } from "../../../../../../utils/highlight";


export default function Users() {
    const { setPageTitle, appData, setPageUserID } = useAppContext();

    const [usersLoading, setUsersLoading] = useState(false);
    const [usersErrorMessage, setUsersErrorMessage] = useState(null);

    const [userList, setUserList] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [licenseAmount, setLicenseAmount] = useState("");
    const [pageProps, setPageProps] = useState({
        page: 1,
        totalPages: 1,
        totalEntries: 0,
        entryLimit: 0,
        visiblePages: [],
    });

    const [activeFilters, setActiveFilters] = useState(["all"]);

    const [pageFilters, setPageFilters] = useState({
        page: null,
        fullname: null,
        displayname: null,
        email: null,
        phone: null,
        status: null,
        super: null,
        trusted: null,
    });

    const raw = localStorage.getItem("userData");
    const userData = raw ? JSON.parse(raw) : null;

    const [toastConfig, setToastConfig] = useState({
        visible: false,
        message: "",
        type: "primary",
    });

    const shownToasts = new Set();
    const showToast = (id, message, type = "primary") => {
        if (!shownToasts.has(id)) {
            shownToasts.add(id);
            setToastConfig({ visible: true, message, type });

            setTimeout(() => shownToasts.delete(id), 5000);
        }
    };



    const handleFilters = (e) => {
        e.preventDefault();
        const { name } = e.target;

        // status values to track (can only have one active at a time)
        const statusOptions = ["active", "pending", "blocked"];

        if (name === "all") {
            setActiveFilters(["all"]);
            setPageFilters((prev) => ({
                ...prev,
                page: null,
                status: null,
                super: null,
                trusted: null,
            }));
        } else {
            setActiveFilters((prev) => {
                let updated;

                // if it's a status filter, allow only one at a time
                if (statusOptions.includes(name)) {
                    // toggle logic: if already selected, remove it
                    const isAlreadySelected = prev.includes(name);
                    updated = prev.filter((item) => !statusOptions.includes(item)); // remove all statuses

                    if (!isAlreadySelected) {
                        updated.push(name);
                    }
                } else {
                    // toggle trusted/admin filters
                    updated = prev.includes(name)
                        ? prev.filter((item) => item !== name)
                        : [...prev.filter((item) => item !== "all"), name];
                }

                // set page filters based on updated array
                setPageFilters((prevFilters) => ({
                    ...prevFilters,
                    page: null,
                    status: updated.includes("active")
                        ? "active"
                        : updated.includes("pending")
                            ? "pending"
                            : updated.includes("blocked")
                                ? "blocked"
                                : null,
                    super: updated.includes("admin") ? 1 : null,
                    trusted: updated.includes("trusted") ? 1 : null,
                }));

                return updated;
            });
        }
    };


    const handlePageChange = (page) => {
        if (page >= 1 && page <= pageProps.totalPages) {

            setPageProps((prev) => ({
                ...prev,
                page: page,
            }));
            setPageFilters((prev) => ({
                ...prev,
                page: page,
            }));
        }


    };

    const [searchValue, setSetchValue] = useState(null);
    const handleSerchChange = (e) => {
        e.preventDefault();
        const { value } = e.target;
        setSetchValue(value);

        if (value.length >= 3) {
            setPageFilters((prev) => ({
                ...prev,
                page: null,
                fullname: value,
                displayname: value,
                email: value,
                phone: value
            }));
        }

        if (value.length === 0) {
            setPageFilters((prev) => ({
                ...prev,
                page: null,
                fullname: null,
                displayname: null,
                email: null,
                phone: null
            }));
        }

    }

    const handleAccessChange = (e) => {
        const { name, checked, value } = e.target;
        updateUserAccess({ id: value, field: name, value: checked ? 1 : 0 });
    };

    const handleStatusChange = (id, status) => {
        updateUserAccess({ id, field: "status", value: status });
    };

    const handleLicenceUpdate = async (e) => {
        e.preventDefault();
        document.querySelector('#exampleModal .btn-close')?.click();

        const userId = selectedUser?.id;
        const prev_keys = parseInt(selectedUser?.total_keys, 0);
        const amount = parseInt(licenseAmount, 0);
        const total_keys = prev_keys + amount;

        await updateUserAccess({ id: userId, field: "prev_keys", value: prev_keys });
        await updateUserAccess({ id: userId, field: "total_keys", value: total_keys });


    }

    const updateUserAccess = async ({ id, field, value }) => {
        setUsersLoading(true);
        setUsersErrorMessage(null);

        try {
            const response = await UserAccess({ id, [field]: value });

            if (response.status === "success") {
                setUserList((prevList) =>
                    prevList.map((user) =>
                        user.id === id
                            ? {
                                ...user,
                                [field]: value,
                            }
                            : user
                    )
                );
            } else {
                setUsersErrorMessage({
                    sTatus: response.status,
                    message: response.message,
                });
            }
            showToast(`update-${id}-field${field}-${value}`, `${response.message} \n\r updating ${id} ${field} to ${value} `, (response.status) === "error" ? "danger" : response.status);

        } catch (err) {
            setUsersErrorMessage({
                sTatus: "error",
                message: err.message || "Failed to update access.",
            });
        } finally {
            setUsersLoading(false);
        }
    };


    useEffect(() => {
        const fetchUsers = async () => {
            setUsersLoading(true);
            setUsersErrorMessage(null);

            try {
                const response = await UsersData({ pageFilters: pageFilters, appName: appData?.name });

                if (response.status === "success") {

                    if (JSON.stringify(response.data.data) !== JSON.stringify(userList)) {
                        setUserList(response.data.data);

                        setPageProps({
                            page: response.data.page,
                            totalPages: response.data.total_pages,
                            totalEntries: response.data.total_records,
                            entryLimit: response.data.limit,
                            visiblePages: response.data.visible_pages,
                        })
                    }

                } else {
                    setUsersErrorMessage({ sTatus: response.status, message: response.message });
                }
            } catch (err) {
                setUsersErrorMessage({ sTatus: "error", message: err.message || "Failed to load users." });
            } finally {
                setUsersLoading(false);
            }
        };
        
        fetchUsers();
    }, [pageFilters, appData, userList]);


    useEffect(() => {
        setPageUserID(null);
        setPageTitle("Users");
    }, [setPageTitle, setPageUserID]);

    

    return (
        <UserPageLayout>
            <Toast message={toastConfig.message}
                type={toastConfig.type}
                visible={toastConfig.visible}
                setVisible={(val) => setToastConfig((prev) => ({ ...prev, visible: val }))}
            />

            <div className="content-body">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-xl-12">
                            <div className="d-flex mb-4 justify-content-between align-items-center flex-wrap">
                                <div className="card-tabs mt-3 mt-sm-0 mb-xxl-0 mb-4">
                                    <ul className="nav nav-tabs" role="tablist">
                                        <li className="nav-item">
                                            <button
                                                className={`nav-link ${activeFilters.includes("all") ? "active" : ""}`}
                                                onClick={handleFilters}
                                                name="all"
                                            >
                                                All Users ({pageProps.totalEntries})
                                            </button>
                                        </li>
                                        <li className="nav-item">
                                            <button
                                                className={`nav-link ${activeFilters.includes("active") ? "active" : ""}`}
                                                onClick={handleFilters}
                                                name="active"
                                            >
                                                Active
                                            </button>
                                        </li>
                                        <li className="nav-item">
                                            <button
                                                className={`nav-link ${activeFilters.includes("pending") ? "active" : ""}`}
                                                onClick={handleFilters}
                                                name="pending"
                                            >
                                                Pending
                                            </button>
                                        </li>
                                        <li className="nav-item">
                                            <button
                                                className={`nav-link ${activeFilters.includes("blocked") ? "active" : ""}`}
                                                onClick={handleFilters}
                                                name="blocked"
                                            >
                                                Blocked
                                            </button>
                                        </li>
                                        <li className="nav-item">
                                            <button
                                                className={`nav-link ${activeFilters.includes("admin") ? "active" : ""}`}
                                                onClick={handleFilters}
                                                name="admin"
                                            >
                                                Admin
                                            </button>
                                        </li>
                                        <li className="nav-item">
                                            <button
                                                className={`nav-link ${activeFilters.includes("trusted") ? "active" : ""}`}
                                                onClick={handleFilters}
                                                name="trusted"
                                            >
                                                Trusted
                                            </button>
                                        </li>
                                    </ul>
                                </div>
                                <div className="table-search">
                                    <div className="input-group search-area mb-xxl-0 mb-4">
                                        <input type="text" className="form-control" placeholder="Search here" onChange={handleSerchChange} />
                                        <span className="input-group-text"><i className="flaticon-381-search-2"></i></span>
                                    </div>
                                </div>
                                <button type="button" className="btn btn-primary mb-xxl-0 mb-4 radius-btn" disabled={true}><i className="far fa-file-word me-2"></i>Generate Report</button>
                            </div>
                            <div className="tab-content">


                                <div className="tab-pane fade active show" >

                                    <div className="table-responsive ">
                                        <div id="guestTable-all-1_wrapper" className="table-responsive dataTables_wrapper no-footer ">
                                            <table className="table  guest-table display mb-4 dataTablesCard dataTable "  role="grid">
                                                <thead>
                                                    <tr role="row">
                                                        <th className="bg-none">
                                                            {usersLoading ? (
                                                                <div className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-white bg-opacity-75">
                                                                    <div className="spinner-border text-primary" role="status"></div>
                                                                </div>
                                                            ) :
                                                                <></>}
                                                        </th>
                                                    
                                                        <th>User</th>
                                                        <th>Licences</th>
                                                        <th>Contact Info</th>
                                                        <th>Authority</th>
                                                        <th>Created</th>
                                                        <th>Status</th>
                                                        <th className="bg-none"></th>
                                                    </tr>

                                                </thead>
                                                <tbody>
                                                    <tr>

                                                        {!usersLoading && usersErrorMessage ?
                                                            (<td colSpan="8" className="text-center py-4">
                                                                <Alerts type={"danger"} message={usersErrorMessage.message} />
                                                            </td>

                                                            ) : (<></>

                                                            )
                                                        }

                                                    </tr>
                                                    {userList.length > 0 ? (
                                                        userList.map((user, index) => (
                                                            <tr key={user.id}>
                                                                <td>
                                                                    <div className="form-check style-1">
                                                                        <input id={`checkbox-${user.id}`} className="form-check-input" type="checkbox" value={user.id} />
                                                                    </div>
                                                                </td>

                                                                

                                                                <td>
                                                                    <div className="media-bx">
                                                                        <img
                                                                            className="me-3 rounded"
                                                                            src={`${user?.image}`}
                                                                            alt={user.displayname}
                                                                            style={{ width: "40px", height: "40px", objectFit: "cover" }}
                                                                        />
                                                                        <div>
                                                                            <span className="text-primary">#ID-{user.id}</span>
                                                                            <h4 className="mb-0 mt-1">
                                                                                <Link to={`/users/${encodeId(user.id)}/profile`} className="text-black" >{highlightText(user.fullname, searchValue)}</Link>
                                                                            </h4>
                                                                            <span className="fs-14">{highlightText(user.displayname, searchValue)}</span>
                                                                        </div>
                                                                    </div>
                                                                </td>

                                                                <td>
                                                                    <div>
                                                                        <h5>{user.total_keys}</h5>
                                                                        <span className="fs-14">From {user.prev_keys}</span>
                                                                    </div>
                                                                </td>

                                                                <td>
                                                                    <div>
                                                                        <h5>{highlightText(user.email, searchValue)}</h5>
                                                                        <span className="fs-14">{highlightText(user.phone, searchValue)}</span>
                                                                    </div>
                                                                </td>

                                                                <td>
                                                                    <div className="form-check">
                                                                        <input name="super" onChange={handleAccessChange} value={user.id} className="form-check-input" type="checkbox" checked={user.super} disabled={((user.super && user.trusted) || !((userData.user.authority).includes("super") && (userData.user.authority).includes("trusted")))} />
                                                                        <label className="form-check-label ">super</label>
                                                                    </div>
                                                                    <div className="form-check">
                                                                        <input name="trusted" onChange={handleAccessChange} value={user.id} className="form-check-input" type="checkbox" checked={user.trusted} disabled={((user.super && user.trusted) || !((userData.user.authority).includes("super") && (userData.user.authority).includes("trusted")))} />
                                                                        <label className="form-check-label ">trusted</label>
                                                                    </div>
                                                                </td>

                                                                <td>
                                                                    <div>
                                                                        <h5>{new Date(user.created).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'short', day: 'numeric' })}</h5>
                                                                        <span className="fs-14">{new Date(user.created).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                                                    </div>
                                                                </td>

                                                                <td>
                                                                    <div className="dropdown dropstart">
                                                                        <a href="/" onClick={(e) => e.preventDefault()} data-bs-toggle="dropdown" aria-expanded="false">
                                                                            <h5 className={`text-${user.status === "active" ? "success" : user.status === "pending" ? "warning" : "danger"}`}>
                                                                                {user.status}
                                                                            </h5>
                                                                        </a>
                                                                        <div className="dropdown-menu">
                                                                            {(user.status === "pending" || user.status === "blocked") ? (
                                                                                <button
                                                                                    className="dropdown-item text-success"
                                                                                    onClick={() => handleStatusChange(user.id, "active")}
                                                                                >
                                                                                    Activate
                                                                                </button>) : (
                                                                                <button
                                                                                    className="dropdown-item text-danger"
                                                                                    onClick={() => handleStatusChange(user.id, "blocked")}
                                                                                >
                                                                                    Block
                                                                                </button>)}
                                                                        </div>
                                                                    </div>
                                                                </td>

                                                                <td>
                                                                    <div className="dropdown dropstart">
                                                                        <a href="/" onClick={(e) => e.preventDefault()} className="btn-link" data-bs-toggle="dropdown" aria-expanded="false">
                                                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                                <path d="M11 12C11 12.5523 11.4477 13 12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12Z" stroke="#262626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                                                <path d="M18 12C18 12.5523 18.4477 13 19 13C19.5523 13 20 12.5523 20 12C20 11.4477 19.5523 11 19 11C18.4477 11 18 11.4477 18 12Z" stroke="#262626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                                                <path d="M4 12C4 12.5523 4.44772 13 5 13C5.55228 13 6 12.5523 6 12C6 11.4477 5.55228 11 5 11C4.44772 11 4 11.4477 4 12Z" stroke="#262626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                                            </svg>
                                                                        </a>
                                                                        <div className="dropdown-menu">
                                                                        
                                                                            <button className="dropdown-item" data-bs-toggle="modal" data-bs-target="#exampleModal"
                                                                                onClick={() => setSelectedUser(user)}
                                                                            >
                                                                                Add Licences
                                                                            </button>
                                                                            <Link to={`/home/${encodeId(user.id)}`} className="dropdown-item" >Dashboard</Link>
                                                                            <Link to={`/users/${encodeId(user.id)}/profile`} className="dropdown-item" >View Profile</Link>
                                                                            <Link to={`/users/${encodeId(user.id)}/eas`} className="dropdown-item" >Manage EAs</Link>
                                                                            <Link to={`/partner/${encodeId(user.id)}`} className="dropdown-item" >View Partner</Link>

                                                                        </div>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <></>
                                                    )}
                                                </tbody>
                                            </table>
                                            {userList.length === 0 && !usersLoading ?
                                                <div className="d-flex flex-column justify-content-center align-items-center py-5">
                                                    <i className="fa fa-folder-open text-muted mb-3" style={{ fontSize: "4rem" }}></i>
                                                    <h3 className="text-muted">No Users Found</h3>
                                                </div> : <></>}

                                            <Pagination
                                                pageProps={pageProps}
                                                onPageChange={handlePageChange}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>


            <form onSubmit={handleLicenceUpdate} className="modal fade" id="exampleModal" tabIndex="-1" aria-labelledby="exampleModalLabel" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered modal-lg">
                    <div className="modal-content rounded-3 shadow">
                        <div className="modal-header border-0">
                            <h5 className="modal-title fw-semibold" id="exampleModalLabel">{selectedUser?.displayname} </h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>

                        <div className="modal-body">
                            <p className="text-muted mb-4">
                                You are assigning new license keys to a mentor. This action cannot be undone. Make sure you've entered the correct number of keys before proceeding.
                            </p>

                            <div className="row text-center mb-4">
                                <div className="col">
                                    <div className="card border-0  py-3">
                                        <h3 className="mb-1">{selectedUser?.total_keys - selectedUser?.prev_keys || 0}</h3>
                                        <small className="text-muted">New</small>
                                    </div>
                                </div>
                                <div className="col">
                                    <div className="card border-0  py-3">
                                        <h3 className="mb-1">{selectedUser?.prev_keys || 0}</h3>
                                        <small className="text-muted">Previous</small>
                                    </div>
                                </div>
                                <div className="col">
                                    <div className="card border-0  py-3">
                                        <h3 className="mb-1">{selectedUser?.total_keys || 0}</h3>
                                        <small className="text-muted">Total Keys</small>
                                    </div>
                                </div>
                            </div>

                            <div className="mb-3">
                                <label htmlFor="licenseInput" className="form-label fw-semibold">Number of license keys to add</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    id="licenseInput"
                                    placeholder="e.g. 20"
                                    onChange={(e) => setLicenseAmount(e.target.value)}
                                    required={true}

                                />
                            </div>
                        </div>

                        <div className="modal-footer border-0">
                            <button type="button" className="btn btn-outline-secondary" data-bs-dismiss="modal">Cancel</button>
                            <button type="submit" className="btn btn-primary" >Approve</button>
                        </div>
                    </div>
                </div>
            </form>

        </UserPageLayout>
    );
}